import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Globe, LogOut, Menu, X, Plus, Trash2, AlertTriangle, ArrowLeft,
  LayoutDashboard, Compass, CalendarDays, Plane, Shield, ChevronDown
} from "lucide-react";
import { toast } from "sonner";
import api, { travelApi } from "@/lib/api";

const SCHENGEN_COUNTRIES = [
  { id: "austria",        name: "Austria",        flag: "🇦🇹" },
  { id: "belgium",        name: "Belgium",        flag: "🇧🇪" },
  { id: "croatia",        name: "Croatia",        flag: "🇭🇷" },
  { id: "czech_republic", name: "Czech Republic", flag: "🇨🇿" },
  { id: "denmark",        name: "Denmark",        flag: "🇩🇰" },
  { id: "estonia",        name: "Estonia",        flag: "🇪🇪" },
  { id: "finland",        name: "Finland",        flag: "🇫🇮" },
  { id: "france",         name: "France",         flag: "🇫🇷" },
  { id: "germany",        name: "Germany",        flag: "🇩🇪" },
  { id: "greece",         name: "Greece",         flag: "🇬🇷" },
  { id: "hungary",        name: "Hungary",        flag: "🇭🇺" },
  { id: "iceland",        name: "Iceland",        flag: "🇮🇸" },
  { id: "italy",          name: "Italy",          flag: "🇮🇹" },
  { id: "latvia",         name: "Latvia",         flag: "🇱🇻" },
  { id: "liechtenstein",  name: "Liechtenstein",  flag: "🇱🇮" },
  { id: "lithuania",      name: "Lithuania",      flag: "🇱🇹" },
  { id: "luxembourg",     name: "Luxembourg",     flag: "🇱🇺" },
  { id: "malta",          name: "Malta",          flag: "🇲🇹" },
  { id: "netherlands",    name: "Netherlands",    flag: "🇳🇱" },
  { id: "norway",         name: "Norway",         flag: "🇳🇴" },
  { id: "poland",         name: "Poland",         flag: "🇵🇱" },
  { id: "portugal",       name: "Portugal",       flag: "🇵🇹" },
  { id: "romania",        name: "Romania",        flag: "🇷🇴" },
  { id: "slovakia",       name: "Slovakia",       flag: "🇸🇰" },
  { id: "slovenia",       name: "Slovenia",       flag: "🇸🇮" },
  { id: "spain",          name: "Spain",          flag: "🇪🇸" },
  { id: "sweden",         name: "Sweden",         flag: "🇸🇪" },
  { id: "switzerland",    name: "Switzerland",    flag: "🇨🇭" },
];

const ALL_COUNTRIES = [
  ...SCHENGEN_COUNTRIES.map(c => ({ ...c, is_schengen: true })),
  { id: "uk", name: "United Kingdom", flag: "🇬🇧", is_schengen: false },
  { id: "turkey", name: "Turkey", flag: "🇹🇷", is_schengen: false },
];

function ProgressCircle({ daysUsed, daysRemaining }) {
  const pct = Math.min(daysUsed / 90, 1);
  const radius = 80;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - pct);
  let strokeColor = "hsl(160, 70%, 40%)";
  if (pct > 0.75) strokeColor = "hsl(0, 84%, 55%)";
  else if (pct > 0.55) strokeColor = "hsl(32, 95%, 50%)";
  return (
    <div className="relative flex items-center justify-center">
      <svg width={200} height={200} viewBox="0 0 200 200" className="transform -rotate-90">
        <circle cx={100} cy={100} r={radius} fill="none" stroke="hsl(220, 10%, 90%)" strokeWidth={10} />
        <circle cx={100} cy={100} r={radius} fill="none" stroke={strokeColor} strokeWidth={10} strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round" />
      </svg>
      <div className="absolute text-center">
        <span className="text-4xl font-extrabold" style={{ color: strokeColor }}>{daysRemaining}</span>
        <p className="text-xs text-muted-foreground mt-0.5">days left</p>
        <p className="text-[10px] text-muted-foreground">{daysUsed} / 90 used</p>
      </div>
    </div>
  );
}

const NavigatorPage = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [status, setStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formCountry, setFormCountry] = useState("");
  const [formEntry, setFormEntry] = useState("");
  const [formExit, setFormExit] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [l, s] = await Promise.all([travelApi.getLogs(), travelApi.getStatus()]);
      setLogs(l); setStatus(s);
    } catch (e) { toast.error("Failed to load travel data"); }
  }, []);

  useEffect(() => {
    api.getMe().then(loadData).catch(() => navigate("/login")).finally(() => setIsLoading(false));
  }, [navigate, loadData]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!formCountry || !formEntry || !formExit) return toast.error("Fill all fields");
    setSubmitting(true);
    try {
      await travelApi.addLog({ country_id: formCountry, entry_date: formEntry, exit_date: formExit, is_schengen: true });
      toast.success("Trip added"); setShowForm(false); 
      setFormCountry(""); setFormEntry(""); setFormExit("");
      await loadData();
    } catch (err) { toast.error(err.message); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (logId) => {
    try { await travelApi.deleteLog(logId); toast.success("Removed"); await loadData(); }
    catch { toast.error("Delete failed"); }
  };

  if (isLoading) return <div className="min-h-screen flex items-center justify-center"><div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 glass border-b p-4"><div className="max-w-7xl mx-auto flex items-center justify-between"><div className="flex items-center gap-2"><Globe className="h-6 w-6 text-primary" /><span className="font-bold text-lg font-['Manrope']">Nomad Roadmap</span></div><div className="flex gap-2"><Button variant="outline" onClick={() => navigate("/dashboard")}>Dashboard</Button><Button onClick={() => setShowForm(!showForm)}><Plus className="h-4 w-4 mr-1" />Log Trip</Button></div></div></header>
      <main className="max-w-5xl mx-auto px-4 py-8">
        <div className="mb-8"><h1 className="text-2xl md:text-3xl font-bold font-['Manrope'] flex items-center gap-3"><Compass className="text-primary" />Nomad Navigator</h1><p className="text-muted-foreground ml-9">Schengen 90 / 180-day tracker</p></div>
        {status && (
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card className="p-6 flex flex-col items-center justify-center"><ProgressCircle daysUsed={status.days_used} daysRemaining={status.days_remaining} /></Card>
            <Card className="md:col-span-2 p-6 grid sm:grid-cols-2 gap-6">
              <div><p className="text-xs uppercase text-muted-foreground mb-1">Window Period</p><p className="font-bold">{status.window_start} — {status.window_end}</p></div>
              <div><p className="text-xs uppercase text-muted-foreground mb-1">Days In Zone</p><p className="text-3xl font-bold">{status.days_used} / 90</p></div>
              <div><p className="text-xs uppercase text-muted-foreground mb-1">Next Overstay</p><p className={status.next_overstay ? "text-destructive font-bold" : "text-primary font-bold"}>{status.next_overstay || "None — You're clear!"}</p></div>
            </Card>
          </div>
        )}
        {showForm && (
          <Card className="mb-8 p-6 border-primary/20">
            <form onSubmit={handleAdd} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Country ID</label><Input value={formCountry} onChange={e => setFormCountry(e.target.value.toLowerCase())} placeholder="e.g. france" /></div>
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Entry Date</label><Input type="date" value={formEntry} onChange={e => setFormEntry(e.target.value)} /></div>
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Exit Date</label><Input type="date" value={formExit} onChange={e => setFormExit(e.target.value)} /></div>
              <div className="flex items-end"><Button type="submit" disabled={submitting} className="w-full">{submitting ? "Saving..." : "Add Trip"}</Button></div>
            </form>
          </Card>
        )}
        <div className="space-y-3">
          {logs.map(log => (<Card key={log.log_id} className="p-4 flex items-center justify-between"><div><h4 className="font-bold capitalize">{log.country_id}</h4><p className="text-sm text-muted-foreground">{log.entry_date} → {log.exit_date}</p></div><Button variant="ghost" size="icon" onClick={() => handleDelete(log.log_id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></Button></Card>))}
        </div>
      </main>
    </div>
  );
};

export default NavigatorPage;